import React from 'react';
import {
  QAOperationDocumentTypes,
  QAOperationType,
} from '@scale/shared/types/qaOperation/qaOperation';
import { IOperationTypeConfig } from './sharedTypes';
import { SpeedAuditAdditionalParams } from './SpeedAuditAdditionalParameters';
import { intervalToDuration } from 'date-fns';

// shorthand for long name
export type InputChangeHandler = React.ChangeEventHandler<HTMLInputElement>;

export function parseDuration(seconds: number) {
  const duration = intervalToDuration({
    start: 0,
    end: seconds * 1000,
  });
  // we still have to calculate hours manually because date-fns doesn't support hours > 24
  const hh = Math.floor(seconds / 3600);

  return { hh, mm: duration.minutes ?? 0, ss: duration.seconds ?? 0 };
}

function prependZero(n: number): string {
  return n < 10 ? `0${n}` : n.toString();
}

export function durationToString(seconds: number) {
  const isNegative = seconds < 0;
  const { hh, mm, ss } = parseDuration(Math.abs(seconds));
  return `${isNegative ? '-' : ''}${prependZero(hh)}:${prependZero(mm)}:${prependZero(ss)}`;
}

const relatedIdOperationMap: Partial<Record<QAOperationType, IOperationTypeConfig>> = {
  [QAOperationType.CreateScenario]: {
    relatedIdList: { label: `${QAOperationDocumentTypes[QAOperationType.CreateScenario]} ID` },
  },
  [QAOperationType.EditScenario]: {
    relatedIdList: { label: `${QAOperationDocumentTypes[QAOperationType.EditScenario]} ID` },
  },
  [QAOperationType.SpeedAudit]: {
    relatedIdList: { label: `${QAOperationDocumentTypes[QAOperationType.SpeedAudit]}/Subtask ID` },
    additionalParams: SpeedAuditAdditionalParams,
    relatedIdWidth: 6,
  },
  [QAOperationType.AuditTasker]: {
    relatedIdList: {
      label: `Tasker ID / Email`,
      supportsEmail: true,
    },
    manualProjectId: true,
  },
  [QAOperationType.Instructions]: {
    manualProjectId: true,
  },
};

export const useOperationTypeConfig = (operationType: QAOperationType | undefined) => {
  const operationTypeConfig = React.useMemo<IOperationTypeConfig | undefined>(() => {
    return operationType && relatedIdOperationMap[operationType];
  }, [operationType]);
  return operationTypeConfig;
};
